/**
 * HERE and PTV live traffic on the stored driver route (PostHog flag
 * `route-traffic`).
 *
 * gps sends, per leg, the slow stretches as point ranges (`traffic`) and the
 * incidents on that leg. The directions library colors the line from one
 * `congestion_numeric` array per leg, indexed by the leg's own points, and
 * splits the drawn line into legs by its own rule. These helpers turn gps's
 * ranges into arrays that line up with that split.
 */

import type { ExpressionSpecification } from "maplibre-gl"
import type { Leg, RouteInfo } from "./RouteView"

export const ROUTE_TRAFFIC_FLAG = "route-traffic"

/** The first congestion level drawn in colour; below it the line stays blue. */
export const FIRST_COLORED_CONGESTION = 25

/**
 * Route line color by congestion, in the style of Google Maps: the line stays
 * blue up to 25% slower than normal (everyday driving noise), then orange,
 * red, and dark red for a jam. Congestion is gps's level, how many percent
 * slower than the speed without traffic; 0 everywhere without the flag.
 */
export const ROUTE_CONGESTION_COLOR: ExpressionSpecification = [
  "step",
  ["to-number", ["get", "congestion"], 0],
  "#2563EB",
  FIRST_COLORED_CONGESTION,
  "#F29900",
  50,
  "#D93025",
  75,
  "#A50E0E",
]

/**
 * Inclusive point indices into one leg's polyline; level 1-100. `delay` is the
 * seconds this stretch adds versus no traffic; older gps replies lack it.
 */
export type TrafficRange = {
  start: number
  end: number
  level: number
  delay?: number
}

const MIN_SHOWN_DELAY_SECONDS = 60

/** Whole-route delay, from each leg's duration against its duration without traffic. null: a leg lacks `base_duration`. */
const routeDelaySeconds = (legs: Leg[]): number | null => {
  let seconds = 0
  for (const leg of legs) {
    if (typeof leg.base_duration !== "number") return null
    seconds += leg.duration - leg.base_duration
  }
  return seconds
}

/** Delay on the stretches drawn in color. null: a colored stretch lacks `delay` (older gps). */
const coloredDelaySeconds = (legs: Leg[]): number | null => {
  let seconds = 0
  for (const leg of legs) {
    for (const range of leg.traffic ?? []) {
      if (range.level < FIRST_COLORED_CONGESTION) continue
      if (typeof range.delay !== "number") return null
      seconds += range.delay
    }
  }
  return seconds
}

/**
 * The traffic delay the route popup shows. `colored`: only the stretches drawn
 * in color, so the text matches the line. `route`: the whole route. `seconds`
 * is null when there is nothing to show (under a minute or unknown).
 */
export type ShownTrafficDelay = {
  seconds: number | null
  scope: "route" | "colored"
}

export function shownTrafficDelay(
  legs: Leg[] | undefined,
  options: { colors: boolean; trafficFetchedAt?: string | null },
): ShownTrafficDelay {
  const colored =
    options.colors && options.trafficFetchedAt
      ? coloredDelaySeconds(legs ?? [])
      : null
  const scope = colored === null ? "route" : "colored"
  const seconds = colored ?? routeDelaySeconds(legs ?? [])
  return {
    seconds:
      seconds !== null && seconds >= MIN_SHOWN_DELAY_SECONDS ? seconds : null,
    scope,
  }
}

export type TrafficIncident = {
  id: string
  type?: string | null
  criticality?: string | null
  description?: string | null
  valid_until?: string | null
  lat: number
  lng: number
  /** PTV only: delay it causes [s] and when the truck gets there (ISO) */
  delay_s?: number | null
  arrives_at?: string | null
}

export type RouteTrafficSettings = {
  colors: boolean
  incidentTypes: string[]
}

export const ROUTE_TRAFFIC_OFF: RouteTrafficSettings = {
  colors: false,
  incidentTypes: [],
}

/** One congestion value per point of all legs joined, 0 where it is free. */
export function congestionFromLegTraffic(
  legPointCounts: number[],
  legTraffic: (TrafficRange[] | undefined)[],
): number[] {
  const congestion: number[] = []
  legPointCounts.forEach((count, legIndex) => {
    const leg = new Array<number>(count).fill(0)
    for (const range of legTraffic[legIndex] ?? []) {
      const end = Math.min(range.end, count - 1)
      for (let i = Math.max(range.start, 0); i <= end; i++) {
        leg[i] = range.level
      }
    }
    congestion.push(...leg)
  })
  return congestion
}

const SAME_POINT_TOLERANCE = 1e-5

const isSamePoint = (a: [number, number], b: [number, number]) =>
  Math.abs(a[0] - b[0]) <= SAME_POINT_TOLERANCE &&
  Math.abs(a[1] - b[1]) <= SAME_POINT_TOLERANCE

/**
 * The index of the last point of each leg, found the way
 * `utils.buildRoutelines` of @maplibre/maplibre-gl-directions does it: each
 * waypoint is matched to the first equal point at or after the previous one.
 * When a leg ends where the next one starts, that shared point closes the
 * earlier leg.
 */
export function legBoundaries(
  points: [number, number][],
  waypoints: [number, number][],
): number[] {
  let from = 0
  return waypoints
    .map((waypoint, index) => {
      const found = points
        .slice(from)
        .findIndex((point) => isSamePoint(point, waypoint))
      if (found !== -1) {
        from += found
      } else if (index === waypoints.length - 1) {
        return points.length - 1
      }
      return from
    })
    .slice(1)
}

/** One array per leg, indexed by the leg's own points. */
export function splitCongestionByLeg(
  congestion: (number | null)[],
  boundaries: number[],
): number[][] {
  let start = 0
  return boundaries.map((end) => {
    const leg = congestion.slice(start, end + 1).map((value) => value ?? 0)
    start = end + 1
    return leg
  })
}

/** The traffic button only belongs on driver chat and full-screen maps. */
export function isRouteTrafficMap(options: {
  flagEnabled: boolean
  isSharedMap: boolean
  isAlertMap: boolean
  isSnapshot: boolean
  isAnalyzeOrderMap: boolean
}): boolean {
  return (
    options.flagEnabled &&
    !options.isSharedMap &&
    !options.isAlertMap &&
    !options.isSnapshot &&
    !options.isAnalyzeOrderMap
  )
}

/**
 * Changes whenever the traffic part of a route reply changes, so a reply with
 * the same polylines but new traffic still gets redrawn.
 */
export function routeTrafficFingerprint(
  routeInfo: Pick<RouteInfo, "routes" | "traffic_fetched_at"> | null,
): string {
  const legs = (routeInfo?.routes?.[0]?.legs ?? []).map((leg) => [
    leg.traffic ?? [],
    leg.incidents ?? [],
  ])
  const hasTraffic = legs.some(([traffic, incidents]) =>
    Boolean(traffic.length || incidents.length),
  )
  if (!hasTraffic && !routeInfo?.traffic_fetched_at) {
    return ""
  }
  return JSON.stringify([routeInfo?.traffic_fetched_at ?? null, legs])
}
